import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticleById } from '@/mocks/articles';
import { getCategoryBySlug } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const article = useMemo(() => (id ? getArticleById(id) : undefined), [id]);

  const categorySlug = useMemo(() => {
    if (!article) return '';
    const cat = getCategoryBySlug(
      article.categoryId === 'cat-1' ? 'puberty'
      : article.categoryId === 'cat-2' ? 'self-protection'
      : article.categoryId === 'cat-3' ? 'mental-health'
      : article.categoryId === 'cat-4' ? 'family-education'
      : article.categoryId === 'cat-5' ? 'gender-equality'
      : '',
    );
    return cat?.slug || '';
  }, [article]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h2 className="text-xl text-foreground-700 mb-2">文章未找到</h2>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    image: article.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '17.3关于性教育宣传片',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema, null, 2) }}
      />
      <Navbar />
      <main className="min-h-screen pt-16">
        {/* Article Header */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-6 md:px-10">
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors duration-200">首页</Link>
              <i className="ri-arrow-right-s-line text-xs" />
              <Link to={`/category/${categorySlug}`} className="hover:text-primary-600 transition-colors duration-200">
                {article.categoryName}
              </Link>
              <i className="ri-arrow-right-s-line text-xs" />
              <span className="text-foreground-700 line-clamp-1">{article.title}</span>
            </nav>

            <div className="flex items-center gap-3 text-xs text-foreground-400 mb-4 flex-wrap">
              <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 font-medium whitespace-nowrap">
                {article.categoryName}
              </span>
              <span>{article.publishDate}</span>
              <span>·</span>
              <span>{article.readTime}</span>
              <span>·</span>
              <span className="flex items-center gap-1">
                <i className="ri-user-line" />
                {article.author}
              </span>
            </div>

            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-5">
              {article.title}
            </h1>

            <div className="aspect-[21/9] rounded-xl overflow-hidden mb-0">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-3xl mx-auto px-6 md:px-10">
            <article
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </section>

        {/* Related / Back */}
        <section className="py-10 bg-background-50">
          <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
            <Link
              to={`/category/${categorySlug}`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-100 hover:bg-primary-200 text-primary-700 font-medium text-sm rounded-lg transition-all duration-200 whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" />
              返回 {article.categoryName} 栏目
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}